import React from 'react';
import { Toast } from 'react-bootstrap';
import { AiOutlineClose } from 'react-icons/ai';

import './BayToast.scss';
import BaySpinner from '../BaySpinner';

interface BayToastProps {
	text?: string;
	show?: boolean;
	loading?: boolean;
	className?: string;
	variant?: string;
	externalLinkText?: string;
	delay?: number;
	autoHide?: boolean;
	dismissible?: boolean;
	addLineBreak?: boolean;
	onClose?: () => void;
	onExternalLinkClick?: () => void;
	[key: string]: any;
}

const BayToast: React.FC<BayToastProps> = ({
	text = 'Bay toast',
	show,
	loading,
	className,
	// Pulled out of `rest` on purpose: it must NOT reach <Toast> (see the note
	// at bg= below) and must not spread onto the DOM either. The variant drives
	// styling through the `bay-toast-variant-*` class the caller passes.
	variant: _variant = 'default',
	externalLinkText,
	delay = 5000,
	autoHide = true,
	dismissible = true,
	addLineBreak = false,
	onClose,
	onExternalLinkClick,
	...rest
}) => {
	const uid = Math.floor(Math.random() * 0x100000).toString(16);
	return (
		<Toast
			key={uid}
			animation
			autohide={autoHide}
			// NO `bg={variant}`. react-bootstrap turns that into Bootstrap's
			// `bg-{variant}` utility, and utilities compile `!important` in this
			// build -- so it becomes `background-color: var(--bs-danger) !important`
			// and paints the toast a saturated alert colour that no stylesheet can
			// reach. The variant palette lives in BayToast.scss instead, set through
			// `--bs-toast-bg`/`--bs-toast-color` on `.bay-toast-variant-{variant}`,
			// which `.toast` already reads. That is the softer background the design
			// calls for.
			//
			// This is why the toast rendered solid red after the Aug 2026 sweep: the
			// old `background-color: ... !important` here was only ever tying the
			// utility. Removing the keyword was right; the utility was the bug.
			bsPrefix="toast"
			className={className ? className : ''}
			delay={delay}
			show={show}
			onClose={onClose}
			onExited={onClose}
			{...rest}
		>
			<Toast.Body bsPrefix="toast-body" className="fs-6">
				<div className="d-flex justify-content-between px-2">
					<div className="d-flex justify-content-center align-items-center text-wrap fs-6">
						{loading && <BaySpinner title={' '} />}
						<p className="mb-0">
							{text}
							{externalLinkText && (
								<>
									{addLineBreak && <br />}
									<span
										className={`text-decoration-underline cursor-pointer ${
											addLineBreak ? false : 'mx-1'
										}`}
										style={{ textUnderlinePosition: 'under' }}
										onClick={onExternalLinkClick}
									>
										{externalLinkText}
									</span>
								</>
							)}
						</p>
					</div>
					{dismissible && (
						<div className="cursor-pointer" onClick={onClose}>
							<AiOutlineClose size={18} />
						</div>
					)}
				</div>
			</Toast.Body>
		</Toast>
	);
};

export default BayToast;
